import React, { FC, useContext, useEffect, useMemo, useState } from "react"
import { useSwipeable } from "react-swipeable"
import { Range } from "react-range"
import { graphql, useStaticQuery } from "gatsby"

import { BespokeComponentFragmentFragment, PricingHeroQueryQuery } from "@graphqlTypes"

import { addThousandCommas, addThousandsSeperator, isClientSide } from "@utils"

import ContentfulLpContext from "@context/ContentfulLpContext/ContentfulLpContext"

import ToggleWithText, { TOGGLE_COLORS, TOGGLE_SIZES } from "@components/ToggleWithText/ToggleWithText"
import TextLink, { TEXT_LINK_SIZES } from "@components/TextLink/TextLink"

import LearnMoreToggle from "./LearnMoreToggle/LearnMoreToggle"
import PricingHeroPlan from "./PricingHeroPlan/PricingHeroPlan"

import { PricingHeroHERO } from "./PricingHero.styles"

type PricingHeroJsonPayload = {
  h1: string
}

const PricingHero: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
  const { contentfulJsonData } = useStaticQuery<PricingHeroQueryQuery>(graphql`
    fragment PricingJsonPricesFragment on contentfulJsonDataJsonJsonNodePlansPrices {
      _0
      _25
      _50
      _75
      _100
      _125
      _150
      _175
      _200
      _225
      _250
      _300
      _350
      _400
      _450
      _500
      _600
      _700
      _800
      _900
      _1000
      _1100
      _1200
      _1300
      _1400
      _1500
      _1600
      _1700
      _1800
      _1900
      _2000
      _2100
      _2200
      _2300
      _2400
      _2500
      _2600
      _2700
      _2800
      _2900
      _3000
      _3100
      _3200
      _3300
      _3400
      _3500
      _3600
      _3700
      _3800
      _3900
      _4000
      _4100
      _4200
      _4300
      _4400
      _4500
      _4600
      _4700
      _4800
      _4900
      _5000
      _5200
      _5400
      _5600
      _5800
      _6000
      _6200
      _6400
      _6600
      _6800
      _7000
      _7200
      _7400
      _7600
      _7800
      _8000
      _8200
      _8400
      _8600
      _8800
      _9000
      _9200
      _9400
      _9600
      _9800
      _10000
      _10200
      _10400
      _10600
      _10800
      _11000
      _11200
      _11400
      _11600
      _11800
      _12000
      _12200
      _12400
      _12600
      _12800
      _13000
      _13200
      _13400
      _13600
      _13800
      _14000
      _14200
      _14400
      _14600
      _14800
      _15000
      _15200
      _15400
      _15600
      _15800
      _16000
      _16200
      _16400
      _16600
      _16800
      _17000
      _17200
      _17400
      _17600
      _17800
      _18000
      _18200
      _18400
      _18600
      _18800
      _19000
      _19200
      _19400
      _19600
      _19800
      _20000
      _20200
      _20400
      _20600
      _20800
      _21000
      _21200
      _21400
      _21600
      _21800
      _22000
      _22200
      _22400
      _22600
      _22800
      _23000
      _23200
      _23400
      _23600
      _23800
      _24000
      _24200
      _24400
      _24600
      _24800
      _25000
      _25500
      _26000
      _26500
      _27000
      _27500
      _28000
      _28500
      _29000
      _29500
      _30000
      _30500
      _31000
      _31500
      _32000
      _32500
      _33000
      _33500
      _34000
      _34500
      _35000
      _35500
      _36000
      _36500
      _37000
      _37500
      _38000
      _38500
      _39000
      _39500
      _40000
      _40500
      _41000
      _41500
      _42000
      _42500
      _43000
      _43500
      _44000
      _44500
      _45000
      _45500
      _46000
      _46500
      _47000
      _47500
      _48000
      _48500
      _49000
      _49500
      _50000
    }

    fragment PricingHeroPlanFragment on contentfulJsonDataJsonJsonNodePlans {
      baseContacts
      ctaLink
      ctaText
      description
      features {
        text
        tooltip
        tooltipImage {
          images {
            fallback {
              sizes
              src
              srcSet
            }
          }
          height
          layout
          width
        }
      }
      featuresIntro
      id
      maxContacts
      name
      prices {
        ...PricingJsonPricesFragment
      }
      shortDescription
      shortDescriptionTooltip
    }

    query PricingHeroQuery {
      contentfulJsonData(contentful_id: {eq: "45c4tePUA3uIu5WPPfUCez"}) {
        json {
          annualDiscountPercentage
          pricingIncrements
          pricingIncrementsSales
          plans {
            ...PricingHeroPlanFragment
          }
        }
      }
    }
  `)

  const json = useMemo(() => {
    try {
      return JSON.parse(jsonPayload?.internal.content || "") as PricingHeroJsonPayload
    } catch (e) {
      console.error(`Failed to parse JSON Payload: PricingHero (${contentful_id})`)
      return null
    }
  }, [jsonPayload])

  if (!json) return null

  if (!json || !(contentfulJsonData && contentfulJsonData.json)) return null

  const {
    annualDiscountPercentage,
    pricingIncrements,
    pricingIncrementsSales,
    plans,
  } = contentfulJsonData.json

  if (!(annualDiscountPercentage && pricingIncrements && pricingIncrementsSales && plans)) return null

  const {
    isAnnualDiscountApplied,
    isSalesPage,
    setIsAnnualDiscountApplied,
  } = useContext(ContentfulLpContext)

  const [activePlan, setActivePlan] = useState<number>(0)
  const [currentIncrement, setCurrentIncrement] = useState<number>(pricingIncrements[2] || 100)

  const plansToUse = isSalesPage ? plans.slice(1, 4) : plans.slice(0, 3)
  const pricingIncrementsToUse = isSalesPage ? pricingIncrementsSales : pricingIncrements

  const currentInputValue = pricingIncrementsToUse.indexOf(currentIncrement)

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      setActivePlan(currentActivePlan => {
        if (plansToUse && currentActivePlan === plansToUse.length - 1) return currentActivePlan
        return currentActivePlan + 1
      })
    },
    onSwipedRight: () => {
      setActivePlan(currentActivePlan => {
        if (currentActivePlan === 0) return currentActivePlan
        return currentActivePlan - 1
      })
    },
    preventScrollOnSwipe: true
  })

  useEffect(() => {
    if (!isClientSide() || !isSalesPage) return

    setCurrentIncrement(pricingIncrementsSales[15] || 1000)
  }, [isSalesPage])

  return (
    <PricingHeroHERO id="PricingHero" className={isSalesPage ? "is-sales-page" : ""}>
      <div className="container">
        <h1 className="type--headline-bold-6XL" dangerouslySetInnerHTML={{__html: json.h1 }} />
      </div>
      <div className="container">
        <div className="grid">
          <div className="column column--1-4-L contacts-slider">
            <div className="toggle-wrapper">
              <div>
                <ToggleWithText
                  color={TOGGLE_COLORS.ORANGE}
                  options={[
                    {
                      title: "Monthly",
                      onClick: () => setIsAnnualDiscountApplied && setIsAnnualDiscountApplied(false),
                      isActive: !isAnnualDiscountApplied,
                    },
                    {
                      title: "Annual",
                      onClick: () => setIsAnnualDiscountApplied && setIsAnnualDiscountApplied(true),
                      isActive: isAnnualDiscountApplied,
                    },
                  ]}
                  size={TOGGLE_SIZES.DYNAMIC}
                />
                <span className={`annual-discount-badge type--text-medium-XS${isAnnualDiscountApplied ? " is-visible" : ""}`}>
                  -{annualDiscountPercentage}%
                </span>
              </div>
            </div>
            <div className="contacts-slider--content">
              <h6 className="current-increment type--headline-medium-S">
                <span>{addThousandsSeperator(currentIncrement)}</span> <span>contacts helped</span>
              </h6>
              <div className="range-input-wrapper">
                <Range
                  max={pricingIncrementsToUse.length - 1}
                  min={0}
                  onChange={(values) => {
                    const _newIncrement = pricingIncrementsToUse[Number(values[0])]
                    if (_newIncrement === null) return
                    setCurrentIncrement(_newIncrement)
                  }}
                  renderThumb={({ props }) => (
                    <div
                      {...props}
                      className="range-input--thumb"
                    />
                  )}
                  renderTrack={({ props, children }) => (
                    <div
                      {...props}
                      className="range-input--track"
                      style={{
                        "--Range--indicator-width": `${currentInputValue / (pricingIncrementsToUse.length - 1) * 100}%`,
                      }}
                    >
                      {children}
                    </div>
                  )}
                  step={1}
                  values={[currentInputValue]}
                />
              </div>
              <div className="range-legend">
                <span>0</span>
                <span>{addThousandCommas(pricingIncrementsToUse[pricingIncrementsToUse.length - 1] || 0)}</span>
              </div>
              <p className="ticket-count type--text-S margin-bottom--0">
                <span>{addThousandCommas(Math.floor(currentIncrement * 1.5))}</span>
                emails <em>or</em> tickets per month for most&nbsp;customers.
              </p>
              <LearnMoreToggle />
            </div>
          </div>
          <div className="column column--4-13-L pricing">
            <div
              className={`flex active-plan--${activePlan}`}
              style={{
                // OVERRIDDEN at BREAKPOINT >635px in the CSS
                translate:
                  activePlan === 1 ?
                    `calc(var(--container-width) / 2 + var(--PricingHeroPlan--card-width) * -1.5 - var(--PricingHero--plans-gap))` :
                    activePlan === 2 ?
                      `calc(var(--container-width) + var(--PricingHeroPlan--card-width) * -3 - var(--PricingHero--plans-gap) * 2)` :
                      `0`
              }}
              {...swipeHandlers}
            >
              {plansToUse.map((plan, i) => !plan ? null : (
                <PricingHeroPlan
                  key={plan.id || i}
                  annualDiscountPercentage={annualDiscountPercentage}
                  currentIncrement={currentIncrement}
                  hideButton={isSalesPage}
                  isAnnualDiscountApplied={isAnnualDiscountApplied}
                  onClick={() => setActivePlan(i)}
                  {...plan}
                />

              ))}
            </div>
            {!isSalesPage && (
              <div className="ProPlan">
                <div className="gradient-background" />
                <h3 className="type--headline-medium-M margin-bottom--base-1-5">{plans[3]?.name}</h3>
                <p className="type--text-S">
                  <strong>Starts with {addThousandCommas(plans[3]?.baseContacts || 0)} contacts</strong>
                  {" "}
                  <span>and includes additional security, higher plan limits, and dedicated&nbsp;onboarding.</span>
                </p>
                <TextLink href={plans[3]?.ctaLink} size={TEXT_LINK_SIZES.S}>
                  {plans[3]?.ctaText}
                </TextLink>
              </div>
            )}
          </div>
        </div>
      </div>
    </PricingHeroHERO >
  )
}

export default PricingHero
